import dayjs from 'dayjs';
import React from 'react';
import { BsChevronRight } from 'react-icons/bs';
import { HiBriefcase, HiCurrencyRupee } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { formatCareerPathType } from 'shared-utils';
import SkillBadge from 'src/components/recruiterPositions/SkillBadge';
import { useUserContext } from 'src/context/UserProvider';
import { PositionDocument, PositionStatus, UserCareerPathType, WorkingMode } from 'types';
import DrivePositionThreeDotMenu from './DrivePositionThreeDotMenu';

const DrivePositionCard = ({ position }: { position: PositionDocument }) => {
	const navigate = useNavigate();
	const { isAccountManagerLoggedIn } = useUserContext();
	const stats = [
		{
			label: 'Matched',
			style: { backgroundColor: '#FFE5FF', borderColor: '#AC5DD9' },
			value: position?.matchedStudentsCount,
		},
		{
			label: 'Shortlisted',
			style: { backgroundColor: '#E3FFF1', borderColor: '#39D98A' },
			value: position?.shortlistedStudentsCount,
		},
	];

	return (
		<div className="relative isolate">
			{position?.positionStatus !== PositionStatus.CLOSED && (
				<div className="absolute top-2 right-3 z-[1]">
					<DrivePositionThreeDotMenu position={position} />
				</div>
			)}
			<button
				onClick={() => {
					navigate(
						isAccountManagerLoggedIn
							? `/drive-positions/candidates/${position?.company?._id}/${position._id}?role=${encodeURIComponent(
									JSON.stringify([position?.title]),
							  )}`
							: `/drive-positions/candidates/${position._id}?role=${encodeURIComponent(
									JSON.stringify([position?.title]),
							  )}`,
					);
				}}
				className="w-full min-h-[198px] flex flex-col shadow-sm transition-all hover:shadow-up active:scale-[0.98] focus-visible:shadow-up focus-visible:ring-2 focus-visible:outline-none text-left bg-white rounded-[12px] border border-[#C7C9D9]"
			>
				<div className="flex-1 p-3 w-full relative">
					<div className="absolute bottom-3 right-3">
						<div className="relative max-w-[48px] min-w-[48px] min-h-[48px]">
							<img src={'/assets/calender.png'} className="max-w-[200px] w-full h-full min-w-[30%]" />
							<div className="absolute inset-0 flex items-center justify-center bg-opacity-75">
								<div className="text-center flex items-center justify-end flex-col h-full w-full pb-1">
									<p className="text-[#6C6768] items-center font-[600] text-[18px] leading-[20px]">
										{dayjs(position?.registeredDrive?.driveDate).format('DD')}
									</p>
									<p className="text-[#6C6768] items-center font-[600] text-[8px]">
										{dayjs(position?.registeredDrive?.driveDate).format('MMMM')}
									</p>
								</div>
							</div>
						</div>
					</div>
					<div>
						<h6 className="text-[#1C1C28] text-base font-[500]">
							{formatCareerPathType(position?.title as UserCareerPathType)}
							{position?.positionLabel && (
								<span className="text-sm font-semibold  text-gray-600">{` - (${position?.positionLabel})`}</span>
							)}
						</h6>
						<div className="flex items-center gap-2 mt-2 text-[12px] text-[#28293D] font-[500]">
							<div className="flex flex-row gap-1 items-center">
								<div>
									<HiBriefcase className="w-[16px] h-[16px] object-contain text-[#00B7C4] dark:text-[#A9EFF2]" />
								</div>
								<div className="flex items-center">
									{position?.workingMode === WorkingMode.FULL_TIME
										? 'Full-Time'
										: position?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME
										  ? 'Internship + Full-Time'
										  : position?.workingMode}
								</div>
							</div>

							<div className="text-xs font-[500] h-[14px] w-[1px] leading-[16px] bg-[#28293D]"></div>

							<div className="flex flex-row gap-1 items-center">
								<div>
									<HiCurrencyRupee className="w-[16px] h-[16px] object-contain text-[#00B7C4] dark:text-[#A9EFF2]" />
								</div>
								<div className="flex items-center">
									{position.salary.min} - {position.salary.max} LPA
								</div>
							</div>
						</div>
					</div>
					<div className="mt-3">
						<div className="text-[10px] leading-[16px] text-[#7777E7] font-[400] dark:text-[#CCDDFF]">
							Required Skills
						</div>
						{position.positionRequirement?.mustHaveskills ? (
							<div className="flex flex-wrap gap-2 mt-1 w-[85%]">
								{position.positionRequirement.mustHaveskills.map((s, i) => {
									if (i < 6) {
										return <SkillBadge key={i} content={<>{s.name}</>} />;
									}
									return null;
								})}
							</div>
						) : (
							<div className="text-[10px] mt-1 text-[#7777E7] dark:text-[#CCDDFF] italic">*No skills required</div>
						)}
					</div>
				</div>
				{/* Bottom Section */}
				<div className="min-h-[50px] w-full rounded-b-[12px] mt-auto flex justify-between items-center text-[14px] sm:text-[12px] font-[500] px-3 py-2 border-t border-[#E4E4EB] dark:border-[#3F3F3F] bg-[#FAFAFC] dark:bg-[#111E2C]">
					<div className="h-full flex items-center gap-4">
						{stats?.map((stat, i) => (
							<div
								key={i}
								style={{ background: stat.style?.backgroundColor }}
								className="relative pl-2 shadow-up rounded-r-lg pr-[6px] flex items-center"
							>
								<div
									className="absolute left-0 h-full w-[2px] rounded-[2px]"
									style={{ background: stat.style?.borderColor }}
								></div>
								<div>
									<div className="text-[10px] md:text-[12px] dark:text-[#28293D]">{stat.label}</div>
									<div className="text-sm font-medium text-[#28293D]">{stat.value}</div>
								</div>
							</div>
						))}
					</div>
					<div>
						<BsChevronRight size={20} color="#7777E7" />
					</div>
				</div>
			</button>
		</div>
	);
};

export default DrivePositionCard;
